import axios from "axios";
import type {
  ClientPurchase,
  ClientPurchaseListQuery,
  ClientPurchaseListResponse,
  PurchaseEvent,
  RecordPurchasePaidPayload,
} from "@/interfaces/purchases";
import { apiClient } from "@/lib/apiClient";

/** Rethrows the server's message so the drawer can show why it was rejected. */
function rethrowApiError(error: unknown, fallback: string): never {
  if (axios.isAxiosError(error)) {
    const msg = (error.response?.data as { error?: string } | undefined)?.error;
    throw new Error(msg || fallback);
  }
  throw error instanceof Error ? error : new Error(fallback);
}

function cleanQuery(q: object): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === "") continue;
    out[k] = String(v);
  }
  return out;
}

export async function fetchClientPurchases(
  query: ClientPurchaseListQuery = {},
): Promise<ClientPurchaseListResponse> {
  const { data } = await apiClient.get<{ success: boolean } & ClientPurchaseListResponse>(
    "/admin/finance/purchases",
    { params: cleanQuery(query) },
  );
  return data;
}

export async function fetchClientPurchase(id: number): Promise<ClientPurchase> {
  const { data } = await apiClient.get<{ success: boolean; data: ClientPurchase }>(
    `/admin/finance/purchases/${id}`,
  );
  return data.data;
}

/** Records that a Stripe payout reached the bank when the webhook never fired. */
export async function recordPurchasePaid(
  id: number,
  payload: RecordPurchasePaidPayload,
): Promise<void> {
  try {
    await apiClient.post(`/admin/finance/purchases/${id}/record-paid`, payload);
  } catch (error) {
    rethrowApiError(error, "Couldn't record the payout.");
  }
}

/** A purchase's activity feed. GFG-only, like the rest of the finance purchase surface. */
export async function fetchPurchaseEvents(purchaseId: number): Promise<PurchaseEvent[]> {
  const { data } = await apiClient.get<{ success: boolean; data: PurchaseEvent[] }>(
    `/admin/finance/purchases/${purchaseId}/events`,
  );
  return data.data;
}
